import { useState, useEffect } from 'react';
import { Modal } from 'antd';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  Subtitle,
} from '../../components/shared';
import ModalFeedbackView from '../../components/shared/feedback/ModalFeedbackView';
import { useDispatch, useSelector } from 'react-redux';
import { sendSingleRequest } from '../../apis';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectUtils } from '../../store/utils/utils.selector';
import { selectFarmsData } from '../../store/farms/farms.selector';
import {
  numToStr,
  toMillisecond,
  toSecond,
  getUnitOfSpecie,
} from '../../lib/common.helpers';
import { TBusinessType } from '../../entities/general.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { getMostCommonCropType } from '../../lib/farm.helpers';
import SelectCropType from '../../components/farmform/SelectCropType';
import { selectAccount } from '../../store/auth/auth.selector';

interface IFormData {
  type: TBusinessType;
  source: string;
  seed_type: string | null;
  src_line_id?: number | null;
  collection_date: number;
  condition: string;
  weight: number | null;
  stage: number | null;
  flupsy_storage?: boolean;
  has_assessment?: boolean;
}

const defaultForm: IFormData = {
  type: 'MUSSEL',
  source: '',
  seed_type: null,
  src_line_id: undefined,
  collection_date: new Date().setHours(0, 0, 0, 0),
  condition: '',
  weight: null,
  stage: 1,
  flupsy_storage: false,
  has_assessment: false,
};

const conditions = [
  { id: 'Good', label: 'Good', value: 'Good' },
  { id: 'Average', label: 'Average', value: 'Average' },
  { id: 'Bad', label: 'Bad', value: 'Bad' },
];

interface Props {
  visible: boolean;
  title?: string;
  data?: any;
  editId?: number;
  onConfirm: (d: any) => void;
  onCancel: () => void;
}

const SpatStorageModal = ({
  visible,
  title,
  data,
  editId,
  onConfirm,
  onCancel,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);
  const unitType = useSelector(selectAccount)?.display_unit;
  const seedTypeOptions = useSelector(selectUtils)
    .filter(x =>
      ['mussel_seed_type', 'oyster_seed_type', 'seaweed_seed_type'].includes(
        x.type,
      ),
    )
    .map(x => ({
      id: x.id.toString(),
      value: x.name,
      label: x.name,
      type: x.type,
    }));

  const [busType, setBusType] = useState<TBusinessType | undefined>(
    data?.type ? data.type : getMostCommonCropType(farmsData),
  );

  const [form, setForm] = useState(defaultForm);
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState<{
    source: string | null;
    condition: string | null;
    weight: string | null;
  }>({ source: null, condition: null, weight: null });

  const updateForm = (key: keyof IFormData, val: any) => {
    if (['weight', 'src_line_id', 'stage'].includes(key)) {
      val = val.length <= 0 ? null : Number(val);
    }
    setForm({ ...form, [key]: val });
    setErrors({ ...errors, [key]: null });
  };

  const validForm = () => {
    let tmpErr = { ...errors };
    let flag = true;
    if (form.source.length <= 0) {
      tmpErr.source = translate(lang, '_input_source_field');
      flag = false;
    }
    if (!form.condition || form.condition.length <= 0) {
      tmpErr.condition = translate(lang, '_input_condition_field');
      flag = false;
    }
    if (!form.weight || form.weight <= 0) {
      tmpErr.weight = translate(lang, '_input_weight_field');
      flag = false;
    }
    if (!form.stage) {
      dispatch(
        showFeedback({
          type: 'error',
          isMessageModal: true,
          message: translate(lang, '_input_stage_field'),
        }),
      );
      flag = false;
    }
    setErrors(tmpErr);
    if (!flag) return null;

    const res = {
      ...form,
      collection_date: toSecond(form.collection_date),
      type: busType,
    };
    return res;
  };
  const confirmClick = async () => {
    const formData = validForm();
    if (!formData) return;

    setDisabled(true);
    const response = !editId
      ? await sendSingleRequest(formData, 'POST', 'api/farm/spat-storage', true)
      : await sendSingleRequest(
          formData,
          'PUT',
          `api/farm/spat-storage/${editId}`,
          true,
        );
    setDisabled(false);

    if (response.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, response.data?.message ?? 'Success'),
        }),
      );
      onConfirm(response.data);
    } else {
      dispatch(
        showFeedback({
          isMessageModal: true,
          type: 'error',
          message: translate(lang, response.data?.message ?? 'Server error'),
        }),
      );
    }
  };
  useEffect(() => {
    if (data) {
      let tmp: any = { ...defaultForm };
      for (let i in form) {
        if (data[i] !== undefined) tmp[i] = (data as any)[i];
      }
      if (data.collection_date) {
        tmp.collection_date = toMillisecond(data.collection_date);
      }
      tmp.flupsy_storage = data.flupsy_storage ?? false;

      setForm(tmp);
    }
  }, [data]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(
              lang,
              !title ? 'Add new spat storage' : '_edit_spat_storage_data',
            )}
          </Subtitle>
        </div>
        {!busType ? (
          <div className='spat-storage-modal'>
            <div className='mt-32 mb-32 text-center'>
              <Button
                color='blue'
                size={1}
                width='full'
                type='fill'
                onClick={() => setBusType('MUSSEL')}
              >
                {translate(lang, 'Mussel Spat Storage')}
              </Button>
            </div>
            <div className='mb-32 text-center'>
              <Button
                color='blue'
                size={1}
                width='full'
                type='fill'
                onClick={() => setBusType('OYSTER')}
              >
                {translate(lang, 'Oyster Spat Storage')}
              </Button>
            </div>
          </div>
        ) : (
          <div className='spat-storage-modal'>
            <div className='mb-17'>
              <SelectCropType value={busType} onChange={v => setBusType(v)} />
            </div>
            <div className={`mb-17 ${errors.source ? 'invalid-form' : ''}`}>
              <Input
                type='text'
                label={translate(lang, 'Source ID')}
                value={form.source}
                onChange={e => updateForm('source', e.target.value)}
              />
              {errors.source && (
                <div className='invalid-feedback'>{errors.source}</div>
              )}
            </div>

            <div className='mb-17'>
              <Dropdown
                label={translate(lang, 'Seed type')}
                options={seedTypeOptions.filter(
                  o => o.type === busType.toLowerCase() + '_seed_type',
                )}
                value={form.seed_type ?? undefined}
                onChange={v => updateForm('seed_type', v)}
              />
            </div>
            {!editId && (
              <div className='mb-17'>
                <CheckboxButton
                  label={translate(lang, '_spat_delivered_flupsy')}
                  onChange={v => updateForm('flupsy_storage', v.target.checked)}
                  checked={form.flupsy_storage}
                />
              </div>
            )}
            <Datepicker
              className='mb-17'
              defaultValue={form.collection_date}
              label={translate(lang, 'Collection date')}
              onChange={e =>
                !!e && updateForm('collection_date', e.toDate().getTime())
              }
              required={true}
            />
            <div className={`mb-17 ${errors.condition ? 'invalid-form' : ''}`}>
              <Dropdown
                onChange={v => updateForm('condition', v)}
                label={translate(lang, 'Condition')}
                options={conditions}
                value={form.condition}
              />
              {errors.condition && (
                <div className='invalid-feedback'>{errors.condition}</div>
              )}
            </div>
            <div className={`mb-17 ${errors.weight ? 'invalid-form' : ''}`}>
              <Input
                type='number'
                label={translate(
                  lang,
                  busType === 'OYSTER' && !form.flupsy_storage
                    ? 'Oyster spat count'
                    : 'Weight',
                )}
                value={numToStr(form.weight)}
                onChange={e => updateForm('weight', e.target.value)}
                unit={
                  busType === 'OYSTER' && !form.flupsy_storage
                    ? 'pieces'
                    : getUnitOfSpecie(unitType, 'weight_kg')
                }
              />
              {errors.weight && (
                <div className='invalid-feedback'>{errors.weight}</div>
              )}
            </div>
            <div className='mb-17'>
              <Input
                type='number'
                label={translate(lang, 'Stage')}
                value={numToStr(form.stage)}
                onChange={e => updateForm('stage', e.target.value)}
              />
            </div>
          </div>
        )}
      </div>
      {!!busType && (
        <div className='modal-button d-flex justify-end mt-32 pt-17'>
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={confirmClick}
            disabled={disabled}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      )}
      <ModalFeedbackView />
    </Modal>
  );
};

export default SpatStorageModal;
